import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faUsers,
  faFileCsv,
  faUserEdit,
  faLayerGroup,
  faArrowRight,
  faChartLine,
  faSpinner,
  faSync,
  faFileAlt,
  faUser,
  faUserFriends,
  faUsersCog,
  faChartBar,
  faCog,
  faDatabase,
  faWaveSquare,
  faRocket,
  faBolt,
  faShieldAlt,
  faNetworkWired,
  faServer,
  faCloudDownloadAlt,
  faChartPie,
  faDownload,
  faClipboardList,
  faInfoCircle,
  faExclamationCircle,
  faEdit,
  faEye,
  faEyeSlash
} from "@fortawesome/free-solid-svg-icons";
import "./manageStudents.css";
import "./dashboard.css";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const ManageStudents = () => {
  const navigate = useNavigate();
  const statsRef = useRef(null);
  const cardsRef = useRef([]);
  const actionCardsRef = useRef([]);
  const [animatedStats, setAnimatedStats] = useState({
    totalStudents: 0,
    totalFamilies: 0,
    currentYearStudents: 0,
    currentYearFamilies: 0,
  });
  const [animationProgress, setAnimationProgress] = useState(0);

  // Toggle for showing/hiding details info cards (hidden by default)
  const [showDetails, setShowDetails] = useState(false);

  // PHP API base URL - SAME AS WORKING VERSION
  const PHP_BASE_URL = "https://mhsdev.online/MHS-WEB/api";
  const SUMMARY_API = `${PHP_BASE_URL}/stdSummary.php`;

  const [stats, setStats] = useState({
    totalStudents: 0,
    totalFamilies: 0,
    currentYearStudents: 0,
    currentYearFamilies: 0,
    growthPercentage: 0,
    previousYearStudents: 0,
    previousYearFamilies: 0,
    genderDistribution: [],
    classDistribution: [],
    monthlyAdmissions: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [activeMainSection, setActiveMainSection] = useState("operations");
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [enteredView, setEnteredView] = useState(false);
  const [glowEffect, setGlowEffect] = useState(false);

  // Initialize animation on mount
  useEffect(() => {
    setEnteredView(true);
    const timer = setTimeout(() => setGlowEffect(true), 500);
    
    // Animate stats counter
    if (!loading && stats.totalStudents > 0) {
      animateStats();
    }
    
    return () => clearTimeout(timer);
  }, [loading, stats]);

  // Make action cards visible immediately on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const actionCards = document.querySelectorAll('.manage-students-action-card');
      actionCards.forEach(card => {
        card.classList.add('manage-students-card-visible');
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Make action cards visible when main section changes
  useEffect(() => {
    const timer = setTimeout(() => {
      const actionCards = document.querySelectorAll('.manage-students-action-card');
      actionCards.forEach(card => {
        card.classList.add('manage-students-card-visible');
      });
    }, 200);
    
    return () => clearTimeout(timer);
  }, [activeMainSection]);

  // Animate stats counting effect
  const animateStats = () => {
    const duration = 1500;
    const steps = 60;
    const interval = duration / steps;
    let currentStep = 0;
    
    const animate = () => {
      if (currentStep <= steps) {
        const progress = currentStep / steps;
        setAnimatedStats({
          totalStudents: Math.floor(stats.totalStudents * progress),
          totalFamilies: Math.floor(stats.totalFamilies * progress),
          currentYearStudents: Math.floor(stats.currentYearStudents * progress),
          currentYearFamilies: Math.floor(stats.currentYearFamilies * progress),
        });
        setAnimationProgress(progress);
        currentStep++;
        setTimeout(animate, interval);
      }
    };
    
    animate();
  };

  // Fetch real-time statistics from PHP API - CORRECT API CALL
  const fetchStatistics = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    } else {
      setRefreshLoading(true);
      // Add shake animation to refresh button
      const btn = document.querySelector('.manage-students-refresh-btn');
      if (btn) {
        btn.classList.add('manage-students-shaking');
        setTimeout(() => btn.classList.remove('manage-students-shaking'), 500);
      }
    }

    try {
      const response = await axios.get(SUMMARY_API, {
        params: {
          action: "get-stats",  // CORRECT ACTION NAME
        },
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Summary API Response:", response.data); // Debug log

      if (response.data && response.data.success) {
        const data = response.data.data || {};

        // Extract and format statistics - SAME AS WORKING VERSION
        const newStats = {
          totalStudents: parseInt(data.total_students) || 0,
          totalFamilies: parseInt(data.total_families) || 0,
          currentYearStudents: parseInt(data.current_year_students) || 0,
          currentYearFamilies: parseInt(data.current_year_families) || 0,
          growthPercentage: data.growth_rates?.student_growth || 0,
          // Additional stats from the API
          previousYearStudents: parseInt(data.previous_year_students) || 0,
          previousYearFamilies: parseInt(data.previous_year_families) || 0,
          genderDistribution: data.gender_distribution || [],
          classDistribution: data.class_distribution || [],
          monthlyAdmissions: data.monthly_admissions || [],
        };

        setStats(newStats);
        setLastUpdated(new Date().toLocaleTimeString());
        setError(null);
        
        // Trigger success animation
        if (!showLoading) {
          triggerSuccessAnimation();
        }
      } else {
        throw new Error(response.data?.error || "Failed to fetch statistics");
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setError("Unable to load statistics. Please check the PHP server.");

      // Fallback to sample data for demonstration - SAME AS WORKING VERSION
      setStats({
        totalStudents: 1245,
        totalFamilies: 342,
        currentYearStudents: 48,
        currentYearFamilies: 12,
        growthPercentage: 15.5,
      });
      triggerErrorAnimation();
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  };

  const triggerSuccessAnimation = () => {
    const statsContainer = document.querySelector('.manage-students-quick-stats');
    if (statsContainer) {
      statsContainer.classList.add('manage-students-pulse-success');
      setTimeout(() => statsContainer.classList.remove('manage-students-pulse-success'), 1000);
    }
  };

  const triggerErrorAnimation = () => {
    const statusBar = document.querySelector('.manage-students-connection-status');
    if (statusBar) {
      statusBar.classList.add('manage-students-shake-error');
      setTimeout(() => statusBar.classList.remove('manage-students-shake-error'), 500);
    }
  };

  useEffect(() => {
    fetchStatistics();

    // Set up auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchStatistics(false);
    }, 300000); // 5 minutes
    
    // Add scroll animation triggers
    const handleScroll = () => {
      // Make all cards visible on scroll
      const allCards = document.querySelectorAll('.manage-students-action-card, .manage-students-main-section-card');
      allCards.forEach(card => {
        const rect = card.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.9) {
          card.classList.add('manage-students-card-visible');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    // Initial check to make all cards visible
    handleScroll();
    
    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleRefresh = () => {
    fetchStatistics(false);
  };

  // Main section cards (both Operations and Reports are now active)
  const mainSectionCards = [
    {
      id: "operations",
      title: "Student Operations",
      subtitle: "Add, edit, and manage student data",
      icon: faUsersCog,
      colorClass: "manage-students-card-operations",
      description: "Comprehensive student data management system",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      accentColor: "#667eea"
    },
    {
      id: "reports",
      title: "Analytics & Reports",
      subtitle: "View and analyze student data",
      icon: faChartBar,
      colorClass: "manage-students-card-reports",
      description: "Advanced analytics and reporting tools",
      gradient: "linear-gradient(135deg, #38a169 0%, #2f855a 100%)",
      accentColor: "#38a169"
    }
  ];

  // Operation Cards - SAME PATHS AS WORKING VERSION
  const operationCards = [
    {
      id: 1,
      title: "Add Student Manually",
      subtitle: "Register individual student records",
      icon: faUserPlus,
      path: "/addStudent",
      colorClass: "manage-students-card-add-studentO",
      gradient: "linear-gradient(135deg, #8dcda6 0%, #6bbf8a 100%)",
      accentColor: "#8dcda6",
      animation: "manage-students-bounceIn",
      delay: 0.1
    },
    {
      id: 2,
      title: "Create a Family",
      subtitle: "Set up family group with multiple students",
      icon: faUsers,
      path: "/Family-Form",
      colorClass: "manage-students-card-add-family",
      gradient: "linear-gradient(135deg, #f0ed96 0%, #e8e56b 100%)",
      accentColor: "#f0ed96",
      animation: "manage-students-bounceIn",
      delay: 0.2
    },
    {
      id: 3,
      title: "Add Students from CSV",
      subtitle: "Bulk import student data",
      icon: faFileCsv,
      path: "/addStudentsCSV",
      colorClass: "manage-students-card-add-csvO",
      gradient: "linear-gradient(135deg, #b1e8e6 0%, #90d6d4 100%)",
      accentColor: "#b1e8e6",
      animation: "manage-students-bounceIn",
      delay: 0.3
    },
    {
      id: 4,
      title: "Add Family from CSV",
      subtitle: "Bulk import family data",
      icon: faFileCsv,
      path: "/Family-Form-CSV",
      colorClass: "manage-students-card-addF-csvO",
      gradient: "linear-gradient(135deg, #ffc0c3 0%, #ffa8ac 100%)",
      accentColor: "#ffc0c3",
      animation: "manage-students-bounceIn",
      delay: 0.4
    },
    {
      id: 5,
      title: "Edit Students",
      subtitle: "Modify existing student records",
      icon: faUserEdit,
      path: "/Edit-Students-Data",
      colorClass: "manage-students-card-edit-studentO",
      gradient: "linear-gradient(135deg, #777070 0%, #5a5454 100%)",
      accentColor: "#777070",
      animation: "manage-students-bounceIn",
      delay: 0.5
    },
    {
      id: 6,
      title: "Manage Sections",
      subtitle: "Organize class sections",
      icon: faLayerGroup,
      path: "/manageSections",
      colorClass: "manage-students-card-instructO",
      gradient: "linear-gradient(135deg, #ff6a6a 0%, #ff5252 100%)",
      accentColor: "#ff6a6a",
      animation: "manage-students-bounceIn",
      delay: 0.6
    },
  ];

  // Report Cards (now fully uncommented)
  const reportCards = [
    {
      id: "all-students",
      title: "All Students Report",
      subtitle: "Complete list of all registered students",
      icon: faUsers,
      path: "/All-Students",
      colorClass: "manage-students-card-report-all",
      gradient: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
      accentColor: "#4299e1",
      animation: "manage-students-slideInRight",
      delay: 0.1
    },
    // {
    //   id: "solo-students",
    //   title: "Solo Students Report",
    //   subtitle: "Students registered individually (not in families)",
    //   icon: faUser,
    //   path: "/students-report/solo",
    //   colorClass: "manage-students-card-report-solo",
    //   gradient: "linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)",
    //   accentColor: "#ed8936",
    //   animation: "manage-students-slideInRight",
    //   delay: 0.2
    // },
    // {
    //   id: "family-students",
    //   title: "Family Students Report",
    //   subtitle: "Students registered through family groups",
    //   icon: faUserFriends,
    //   path: "/students-report/family",
    //   colorClass: "manage-students-card-report-family",
    //   gradient: "linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)",
    //   accentColor: "#9f7aea",
    //   animation: "manage-students-slideInRight",
    //   delay: 0.3
    // },
    // {
    //   id: "detailed-analytics",
    //   title: "Detailed Analytics",
    //   subtitle: "Advanced statistics and insights",
    //   icon: faChartLine,
    //   path: "/students-report/analytics",
    //   colorClass: "manage-students-card-report-analytics",
    //   gradient: "linear-gradient(135deg, #38a169 0%, #2f855a 100%)",
    //   accentColor: "#38a169",
    //   animation: "manage-students-slideInRight",
    //   delay: 0.4
    // },
    // {
    //   id: "class-wise",
    //   title: "Class-wise Distribution",
    //   subtitle: "Students organized by class and section",
    //   icon: faLayerGroup,
    //   path: "/students-report/class",
    //   colorClass: "manage-students-card-report-class",
    //   gradient: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
    //   accentColor: "#f56565",
    //   animation: "manage-students-slideInRight",
    //   delay: 0.5
    // },
    {
      id: "export-reports",
      title: "Export Reports (UPCOMING)",
      subtitle: "Download reports in various formats",
      icon: faFileAlt,
      path: "",
      colorClass: "manage-students-card-report-export",
      gradient: "linear-gradient(135deg, #718096 0%, #4a5568 100%)",
      accentColor: "#718096",
      animation: "manage-students-slideInRight",
      delay: 0.6
    }
  ];

  const handleMainSectionClick = (sectionId) => {
    // Add section switch animation
    const sectionElement = document.querySelector('.manage-students-dynamic-content-section');
    if (sectionElement) {
      sectionElement.classList.add('manage-students-fade-out');
      setTimeout(() => {
        setActiveMainSection(sectionId);
        setActiveCard(null);
        sectionElement.classList.remove('manage-students-fade-out');
        sectionElement.classList.add('manage-students-fade-in');
        setTimeout(() => sectionElement.classList.remove('manage-students-fade-in'), 300);
      }, 150);
    } else {
      setActiveMainSection(sectionId);
      setActiveCard(null);
    }
  };

  const handleCardClick = (path, id) => {
    // Ripple effect animation
    const card = document.querySelector(`[data-card-id="${id}"]`);
    if (card) {
      const ripple = document.createElement('span');
      const rect = card.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = rect.width / 2;
      const y = rect.height / 2;
      
      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = x - size / 2 + 'px';
      ripple.style.top = y - size / 2 + 'px';
      ripple.classList.add('manage-students-ripple');
      
      card.appendChild(ripple);
      
      setTimeout(() => {
        ripple.remove();
      }, 600);
    }
    
    setActiveCard(id);
    setTimeout(() => {
      navigate(path);
    }, 300);
  };

  const handleCardHover = (id) => {
    setHoveredCard(id);
    setActiveCard(id);
  };

  const handleCardLeave = () => {
    setHoveredCard(null);
    setActiveCard(null);
  };

  const formatNumber = (num) => {
    return num.toLocaleString("en-US");
  };

  const calculatePercentage = (current, total) => {
    if (total === 0) return 0;
    return ((current / total) * 100).toFixed(1);
  };

  return (
    <div className="manage-students-dashboard-wrapper">
      <Sidebar />

      <div className="manage-students-main-content" style={{marginLeft:'18rem'}}>
        {/* Professional Header with Animation */}
        <header className={`manage-students-header ${enteredView ? 'manage-students-slide-in' : ''}`}>
          <div className="manage-students-header-content">
            <div className="manage-students-header-text">
              <h1>
                <span className="manage-students-title-gradient">Student Management</span>
                <span className="manage-students-title-sub">Professional Dashboard</span>
              </h1>
              <p className="manage-students-header-description">
                <FontAwesomeIcon icon={faWaveSquare} className="manage-students-pulse-icon" />
                Comprehensive student management system with live statistics
              </p>
            </div>
            <div className={`manage-students-dashboard-badge ${glowEffect ? 'manage-students-glow' : ''}`}>
              <div className="manage-students-badge-icon">
                <FontAwesomeIcon icon={faRocket} />
              </div>
              <div className="manage-students-badge-text">
                <span className="manage-students-badge-title">Live Dashboard</span>
                <span className="manage-students-badge-subtitle">Real-time Statistics</span>
              </div>
            </div>
          </div>
        </header>

        {/* Connection Status Bar - FIXED API MESSAGE */}
        <div className={`manage-students-connection-status ${error ? 'manage-students-error' : 'manage-students-connected'}`}>
          <div className="manage-students-status-bar">
            <div className="manage-students-status-indicator">
              <div className={`manage-students-status-dot ${error ? 'manage-students-offline manage-students-pulse' : 'manage-students-online manage-students-pulse'}`}></div>
              <span className="manage-students-status-text">
                {error ? (
                  <>
                    <FontAwesomeIcon icon={faExclamationCircle} /> PHP Server Offline - Using Sample Data
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faNetworkWired}  style={{marginLeft:'18vh'}}/> PHP Server Connected
                  </>
                )}
              </span>
            </div>
            <div className="manage-students-status-controls">
              {lastUpdated && (
                <span className="manage-students-last-updated">
                  <FontAwesomeIcon icon={faSync} />
                  Updated: {lastUpdated}
                </span>
              )}
              <button
                onClick={handleRefresh}
                disabled={refreshLoading}
                className={`manage-students-refresh-btn ${refreshLoading ? 'manage-students-loading' : ''}`}
              >
                <FontAwesomeIcon
                  icon={refreshLoading ? faSpinner : faSync}
                  spin={refreshLoading}
                  className="manage-students-refresh-icon"
                />
                {refreshLoading ? 'Refreshing...' : 'Refresh'}
              </button>

              {/* Toggle button for details info cards */}
              <button
                className="manage-students-toggle-details-btn"
                onClick={() => setShowDetails(!showDetails)}
                title={showDetails ? "Hide statistics" : "Show statistics"}
              >
                <FontAwesomeIcon icon={showDetails ? faEyeSlash : faEye} />
                <span>{showDetails ? "Hide Details" : "Show Details"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats & Stats Breakdown - hidden by default */}
        {showDetails && (
          <>
            {/* Quick Stats with Animated Counters */}
            <div className="manage-students-quick-stats" ref={statsRef}>
              <div className="manage-students-stat-card manage-students-animate-on-scroll">
                <div className="manage-students-stat-icon">
                  <FontAwesomeIcon icon={faUserFriends} style={{color:'red'}}/>
                </div>
                <div className="manage-students-stat-content">
                  <div className="manage-students-stat-value">
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <>
                        {formatNumber(animatedStats.totalStudents)}
                        <span className="manage-students-stat-trend manage-students-up">+{stats.growthPercentage}%</span>
                      </>
                    )}
                  </div>
                  <div className="manage-students-stat-label">
                    Total Students
                    <div className="manage-students-stat-subtext">All Time Records</div>
                  </div>
                </div>
                <div className="manage-students-stat-progress">
                  <div 
                    className="manage-students-progress-bar" 
                    style={{width: `${animationProgress * 100}%`}}
                  ></div>
                </div>
              </div>

              <div className="manage-students-stat-card manage-students-animate-on-scroll" style={{animationDelay: '0.1s'}}>
                <div className="manage-students-stat-icon">
                  <FontAwesomeIcon icon={faUsers} style={{color:'green'}}/>
                </div>
                <div className="manage-students-stat-content">
                  <div className="manage-students-stat-value">
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      formatNumber(animatedStats.currentYearStudents)
                    )}
                  </div>
                  <div className="manage-students-stat-label">
                    Current Year
                    <div className="manage-students-stat-subtext">{new Date().getFullYear()} Admissions</div>
                  </div>
                </div>
                {stats.growthPercentage > 0 && (
                  <div className="manage-students-growth-badge manage-students-pulse">
                    +{stats.growthPercentage}%
                  </div>
                )}
              </div>

              <div className="manage-students-stat-card manage-students-animate-on-scroll" style={{animationDelay: '0.2s'}}>
                <div className="manage-students-stat-icon">
                  <FontAwesomeIcon icon={faUser} style={{color:'blue'}}/>
                </div>
                <div className="manage-students-stat-content">
                  <div className="manage-students-stat-value">
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      formatNumber(animatedStats.totalFamilies)
                    )}
                  </div>
                  <div className="manage-students-stat-label">
                    Total Families
                    <div className="manage-students-stat-subtext">Registered Groups</div>
                  </div>
                </div>
              </div>

              <div className="manage-students-stat-card manage-students-animate-on-scroll" style={{animationDelay: '0.3s'}}>
                <div className="manage-students-stat-icon">
                  <FontAwesomeIcon icon={faUserPlus} style={{color:'orange'}}/>
                </div>
                <div className="manage-students-stat-content">
                  <div className="manage-students-stat-value">
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      formatNumber(animatedStats.currentYearFamilies)
                    )}
                  </div>
                  <div className="manage-students-stat-label">
                    Current Year
                    <div className="manage-students-stat-subtext">{new Date().getFullYear()} Families</div>
                  </div>
                </div>
                <div className="manage-students-percentage-badge">
                  {calculatePercentage(stats.currentYearFamilies, stats.totalFamilies)}%
                  <div className="manage-students-badge-arrow manage-students-up"></div>
                </div>
              </div>
            </div>

            {/* Stats Breakdown Section */}
            {!loading && !error && (
              <div className="manage-students-stats-breakdown manage-students-animate-on-scroll">
                <div className="manage-students-breakdown-item">
                  <div className="manage-students-breakdown-icon">
                    <FontAwesomeIcon icon={faChartLine} />
                  </div>
                  <div className="manage-students-breakdown-content">
                    <div className="manage-students-breakdown-label">Student/Family Ratio</div>
                    <div className="manage-students-breakdown-value">
                      {stats.totalFamilies > 0
                        ? (stats.totalStudents / stats.totalFamilies).toFixed(1)
                        : 0}
                      <span className="manage-students-breakdown-unit">:1</span>
                    </div>
                    <div className="manage-students-breakdown-subtext">Students per family</div>
                  </div>
                </div>

                <div className="manage-students-breakdown-item">
                  <div className="manage-students-breakdown-icon">
                    <FontAwesomeIcon icon={faBolt} />
                  </div>
                  <div className="manage-students-breakdown-content">
                    <div className="manage-students-breakdown-label">New Admissions Rate</div>
                    <div className="manage-students-breakdown-value">
                      {calculatePercentage(stats.currentYearStudents, stats.totalStudents)}%
                    </div>
                    <div className="manage-students-breakdown-subtext">of total students</div>
                  </div>
                </div>

                <div className="manage-students-breakdown-item">
                  <div className="manage-students-breakdown-icon">
                    <FontAwesomeIcon icon={faShieldAlt} />
                  </div>
                  <div className="manage-students-breakdown-content">
                    <div className="manage-students-breakdown-label">Year-over-Year</div>
                    <div className={`manage-students-breakdown-value ${stats.growthPercentage > 0 ? "manage-students-positive" : "manage-students-negative"}`}>
                      {stats.growthPercentage > 0 ? "+" : ""}
                      {stats.growthPercentage}%
                    </div>
                    <div className="manage-students-breakdown-subtext">Growth rate</div>
                  </div>
                </div>

                <div className="manage-students-breakdown-item">
                  <div className="manage-students-breakdown-icon">
                    <FontAwesomeIcon icon={faSync} />
                  </div>
                  <div className="manage-students-breakdown-content">
                    <div className="manage-students-breakdown-label">Data Freshness</div>
                    <div className="manage-students-breakdown-value">Live</div>
                    <div className="manage-students-breakdown-subtext">Real-time sync</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Main Section Selection */}
        <section 
        className="manage-students-main-section-selector manage-students-animate-on-scroll">
          <h2 className="manage-students-section-title">
            <FontAwesomeIcon icon={faCog} className="manage-students-section-icon manage-students-spin-slow" />
            Select Module
            <span className="manage-students-section-badge">{mainSectionCards.length} Modules</span>
          </h2>
          <div className="manage-students-main-section-grid" >
            {mainSectionCards.map((card, index) => (
              <div
                key={card.id}
                ref={el => cardsRef.current[index] = el}
                className={`manage-students-main-section-card ${card.colorClass} ${
                  activeMainSection === card.id ? "manage-students-active" : ""
                }`}
                onClick={() => handleMainSectionClick(card.id)}
                onMouseEnter={() => handleCardHover(card.id)}
                onMouseLeave={handleCardLeave}
                tabIndex="0"
                role="button"
                aria-label={`Select ${card.title}`}
                data-tooltip={card.description}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  '--card-gradient': card.gradient,
                  '--accent-color': card.accentColor
                }}
              >
                <div className="manage-students-card-glow"></div>
                <div className="manage-students-main-section-icon">
                  <FontAwesomeIcon icon={card.icon} />
                </div>
                <h3 className="manage-students-main-section-title">{card.title}</h3>
                <p className="manage-students-main-section-subtitle">{card.subtitle}</p>
                {activeMainSection === card.id && (
                  <div className="manage-students-section-indicator">
                    <div className="manage-students-indicator-dot manage-students-pulse"></div>
                    <span>Active</span>
                  </div>
                )}
                <div className="manage-students-card-arrow">
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Dynamic Content Section - now includes both Operations and Reports */}
        <section 
        className="manage-students-dynamic-content-section">
          {activeMainSection === "operations" ? (
            <>
              <h2 className="manage-students-section-title">
                <FontAwesomeIcon icon={faUsersCog} className="manage-students-section-icon manage-students-bounce" />
                Student Operations
                <span className="manage-students-section-counter">{operationCards.length} Actions</span>
              </h2>
              <div className="manage-students-actions-grid">
                {operationCards.map((card) => (
                  <div
                    key={card.id}
                    data-card-id={card.id}
                    className={`manage-students-action-card ${card.colorClass} ${
                      activeCard === card.id ? "manage-students-active" : ""
                    } ${hoveredCard === card.id ? "manage-students-hovered" : ""}`}
                    onClick={() => handleCardClick(card.path, card.id)}
                    onMouseEnter={() => handleCardHover(card.id)}
                    onMouseLeave={handleCardLeave}
                    tabIndex="0"
                    role="button"
                    aria-label={`Navigate to ${card.title}`}
                    data-tooltip={card.description}
                    style={{
                      animationDelay: `${card.delay}s`,
                      '--card-gradient': card.gradient,
                      '--accent-color': card.accentColor
                    }}
                  >
                    <div className="manage-students-card-glow"></div>
                    <div className="manage-students-action-icon">
                      <FontAwesomeIcon icon={card.icon} />
                    </div>
                    <h3 className="manage-students-action-title">{card.title}</h3>
                    <p className="manage-students-action-subtitle">{card.subtitle}</p>
                    <div className="manage-students-card-hover-content">
                      <span>Click to Open</span>
                      <FontAwesomeIcon icon={faArrowRight} className="manage-students-arrow-animate" />
                    </div>
                    <div className="manage-students-card-footer">
                      <div className="manage-students-card-tags">
                        <span className="manage-students-tag">Quick Action</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Reports section (now uncommented)
            <>
              <h2 className="manage-students-section-title">
                <FontAwesomeIcon icon={faChartBar} className="manage-students-section-icon manage-students-bounce" />
                Analytics & Reports
                <span className="manage-students-section-counter">{reportCards.length} Reports</span>
              </h2>
              <div className="manage-students-actions-grid">
                {reportCards.map((card) => (
                  <div
                    key={card.id}
                    data-card-id={card.id}
                    className={`manage-students-action-card ${card.colorClass} ${
                      activeCard === card.id ? "manage-students-active" : ""
                    } ${hoveredCard === card.id ? "manage-students-hovered" : ""}`}
                    onClick={() => handleCardClick(card.path, card.id)}
                    onMouseEnter={() => handleCardHover(card.id)}
                    onMouseLeave={handleCardLeave}
                    tabIndex="0"
                    role="button"
                    aria-label={`Navigate to ${card.title}`}
                    data-tooltip={card.description}
                    style={{
                      animationDelay: `${card.delay}s`,
                      '--card-gradient': card.gradient,
                      '--accent-color': card.accentColor
                    }}
                  >
                    <div className="manage-students-card-glow"></div>
                    <div className="manage-students-action-icon">
                      <FontAwesomeIcon icon={card.icon} />
                    </div>
                    <h3 className="manage-students-action-title">{card.title}</h3>
                    <p className="manage-students-action-subtitle">{card.subtitle}</p>
                    <div className="manage-students-card-hover-content">
                      <span>View Report</span>
                      <FontAwesomeIcon icon={faArrowRight} className="manage-students-arrow-animate" />
                    </div>
                    <div className="manage-students-card-footer">
                      <div className="manage-students-card-tags">
                        <span className="manage-students-tag">Analytics</span>
                        <span className="manage-students-tag">Export</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        {/* System Information */}
        <div className="manage-students-system-info manage-students-animate-on-scroll">
          <h3>
            <FontAwesomeIcon icon={faDatabase} className="manage-students-spin-slow" />
            System Information
            <span className="manage-students-info-badge">Live</span>
          </h3>
          <div className="manage-students-info-grid">
            <div className="manage-students-info-item">
              <div className="manage-students-info-icon">
                <FontAwesomeIcon icon={faServer} />
              </div>
              <div className="manage-students-info-content">
                <div className="manage-students-info-title">PHP Backend</div>
                <div className="manage-students-info-desc">stdSummary.php API</div>
              </div>
            </div>
            <div className="manage-students-info-item">
              <div className="manage-students-info-icon">
                <FontAwesomeIcon icon={faSync} />
              </div>
              <div className="manage-students-info-content">
                <div className="manage-students-info-title">Real-time Data</div>
                <div className="manage-students-info-desc">Auto-refresh every 5 min</div>
              </div>
            </div>
            <div className="manage-students-info-item">
              <div className="manage-students-info-icon">
                <FontAwesomeIcon icon={faNetworkWired} />
              </div>
              <div className="manage-students-info-content">
                <div className="manage-students-info-title">Database</div>
                <div className="manage-students-info-desc">school_results (MySQL)</div>
              </div>
            </div>
            <div className="manage-students-info-item">
              <div className="manage-students-info-icon">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <div className="manage-students-info-content">
                <div className="manage-students-info-title">Records</div>
                <div className="manage-students-info-desc">{formatNumber(stats.totalStudents)} Active</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="manage-students-footer">
          <div className="manage-students-footer-content">
            <div className="manage-students-footer-brand">
              <div className="manage-students-brand-logo">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <div className="manage-students-brand-text">
                <div className="manage-students-brand-name">Student Management System</div>
                <div className="manage-students-brand-version">Professional Edition v2.2.0</div>
              </div>
            </div>
            <div className="manage-students-footer-details">
              <div className="manage-students-detail-item">
                <FontAwesomeIcon icon={faSync} />
                <span>Last updated: {lastUpdated || "Loading..."}</span>
              </div>
              <div className="manage-students-detail-item">
                <FontAwesomeIcon icon={faDatabase} />
                <span>{formatNumber(stats.totalStudents)} Active Records</span>
              </div>
              <div className="manage-students-detail-item">
                <FontAwesomeIcon icon={faUsers} />
                <span>{formatNumber(stats.totalFamilies)} Family Groups</span>
              </div>
            </div>
          </div>
          <div className="manage-students-footer-copyright">
            <p>© {new Date().getFullYear()} My School. All Rights Reserved.</p>
            <div className="manage-students-footer-links">
              <span>Privacy Policy</span>
              <span>•</span>
              <span>Terms of Service</span>
              <span>•</span>
              <span>Support</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ManageStudents;